// frontend/src/components/AdminProducts.js
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";

const MARKETPLACE_ITEMS_CACHE_KEY = "marketplace-items-cache-v1";

const emptyEditForm = {
  name: "",
  description: "",
  price: "",
  stockQty: "",
  category: "",
  productType: "accessory",
  salesChannel: "retail",
  listingStatus: "active",
  wholesalePrice: "",
  wholesaleMinQty: "",
  wholesaleNotes: "",
  adminNotes: "",
  featured: false,
  tags: "",
  videoUrl: "",
  imageFile: null,
  shippingScope: "india",
  shippingTamilNadu: "",
  shippingIndia: "",
};

const FILTERS = [
  { value: "all", label: "All listings" },
  { value: "public", label: "Public storefront" },
  { value: "retail", label: "Retail enabled" },
  { value: "wholesale", label: "Wholesale/private" },
  { value: "hidden", label: "Hidden" },
  { value: "review", label: "Needs review" },
  { value: "not-public", label: "Not public" },
  { value: "low-stock", label: "Low stock" },
  { value: "sold-out", label: "Sold out" },
];

const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name A-Z" },
  { value: "price-high", label: "Price high to low" },
  { value: "price-low", label: "Price low to high" },
  { value: "stock-low", label: "Stock low to high" },
  { value: "stock-high", label: "Stock high to low" },
];

const AdminProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [message, setMessage] = useState("");
  const [savingId, setSavingId] = useState("");
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const syncMarketplaceCache = (product) => {
    try {
      const cached = JSON.parse(localStorage.getItem(MARKETPLACE_ITEMS_CACHE_KEY) || "[]");
      if (!Array.isArray(cached)) return;

      const productIsPublic = Boolean(product?.visibility?.public);
      const nextCache = productIsPublic
        ? (() => {
            const normalizedProduct = {
              ...product,
              image: product.imageUrl || product.image || "",
            };
            const exists = cached.some((row) => row._id === product._id);
            if (exists) {
              return cached.map((row) => (
                row._id === product._id
                  ? { ...row, ...normalizedProduct, image: normalizedProduct.image }
                  : row
              ));
            }
            return [normalizedProduct, ...cached];
          })()
        : cached.filter((row) => row._id !== product?._id);

      localStorage.setItem(MARKETPLACE_ITEMS_CACHE_KEY, JSON.stringify(nextCache));
      window.dispatchEvent(new CustomEvent("marketplace-items-cache-updated"));
    } catch (err) {
      console.error("Marketplace cache sync error:", err);
    }
  };

  useEffect(() => () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
  }, [imagePreviewUrl]);

  const fetchItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/admin/products");
      setItems(res.data?.products || []);
    } catch (err) {
      console.error("Admin products error:", err);
      setError(err.response?.data?.error || err.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    setFilter(searchParams.get("filter") || "all");
    setSort(searchParams.get("sort") || "newest");
    setSearch(searchParams.get("q") || "");
  }, [searchParams]);

  const setUrlState = (patch) => {
    const next = {
      filter,
      sort,
      q: search,
      ...patch,
    };
    const params = new URLSearchParams();
    if (next.filter && next.filter !== "all") params.set("filter", next.filter);
    if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
    if (next.q && next.q.trim()) params.set("q", next.q.trim());
    setSearchParams(params);
  };

  const summary = useMemo(() => ({
    total: items.length,
    publicCount: items.filter((item) => item.visibility?.public).length,
    needsAttention: items.filter((item) => !item.visibility?.public).length,
    review: items.filter((item) => (item.listingStatus || "active") === "review").length,
    lowStock: items.filter((item) => Number(item.stockQty || 0) > 0 && Number(item.stockQty || 0) <= 3).length,
    soldOut: items.filter((item) => Number(item.stockQty || 0) <= 0 || item.sold === true).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      const visibility = item.visibility || {};
      const stockQty = Number(item.stockQty || 0);
      const matchesFilter =
        filter === "all" ||
        (filter === "public" && visibility.public) ||
        (filter === "retail" && ["retail", "both"].includes(item.salesChannel || "retail")) ||
        (filter === "wholesale" && ["wholesale", "both"].includes(item.salesChannel)) ||
        (filter === "hidden" && item.listingStatus === "hidden") ||
        (filter === "review" && item.listingStatus === "review") ||
        (filter === "not-public" && !visibility.public) ||
        (filter === "low-stock" && stockQty > 0 && stockQty <= 3) ||
        (filter === "sold-out" && (stockQty <= 0 || item.sold === true));

      const seller = item.sellerId || {};
      const haystack = [
        item.name,
        item.description,
        item.category,
        item.productType,
        item.salesChannel,
        seller.storeName,
        seller.name,
        seller.email,
        seller.city,
        seller.state,
        ...(item.tags || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      return matchesFilter && matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      if (sort === "oldest") return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      if (sort === "price-high") return Number(b.price || 0) - Number(a.price || 0);
      if (sort === "price-low") return Number(a.price || 0) - Number(b.price || 0);
      if (sort === "stock-low") return Number(a.stockQty || 0) - Number(b.stockQty || 0);
      if (sort === "stock-high") return Number(b.stockQty || 0) - Number(a.stockQty || 0);
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [filter, items, search, sort]);

  const updateProduct = async (item, patch) => {
    setSavingId(item._id);
    setMessage("");
    try {
      const isMultipart = typeof FormData !== "undefined" && patch instanceof FormData;
      const res = await api.put(`/api/admin/products/${item._id}`, patch, isMultipart
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined);
      syncMarketplaceCache(res.data.product);
      setItems((prev) =>
        prev.map((row) => (row._id === item._id ? res.data.product : row))
      );
      setMessage(`${item.name} updated. ${res.data.product?.visibility?.reason || ""}`);
      return res.data.product;
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update product");
      return null;
    } finally {
      setSavingId("");
    }
  };

  const startEditProduct = (item) => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setEditProduct(item);
    setImagePreviewUrl("");
    setEditForm({
      name: item.name || "",
      description: item.description || "",
      price: String(item.price || 0),
      stockQty: String(item.stockQty || 0),
      category: item.category || "",
      productType: item.productType || "accessory",
      salesChannel: item.salesChannel || "retail",
      listingStatus: item.listingStatus || "active",
      wholesalePrice: String(item.wholesalePrice || 0),
      wholesaleMinQty: String(item.wholesaleMinQty || 0),
      wholesaleNotes: item.wholesaleNotes || "",
      adminNotes: item.adminNotes || "",
      featured: Boolean(item.featured),
      tags: Array.isArray(item.tags) ? item.tags.join(", ") : "",
      videoUrl: item.videoUrl || "",
      imageFile: null,
      shippingScope: item.shippingScope || (item.productType === "live-fish" ? "tamil-nadu" : "india"),
      shippingTamilNadu: String(item.shippingRates?.TN || item.shippingRates?.["Tamil Nadu"] || 250),
      shippingIndia: String(item.shippingRates?.INDIA || item.shippingRates?.OTHER || 120),
    });
  };

  const saveProductDetails = async (event) => {
    event.preventDefault();
    if (!editProduct) return;
    const productType = editForm.productType;
    const shippingScope = productType === "live-fish" ? "tamil-nadu" : editForm.shippingScope;
    const shippingRates =
      shippingScope === "tamil-nadu"
        ? { TN: Number(editForm.shippingTamilNadu || 0) }
        : { INDIA: Number(editForm.shippingIndia || 0), OTHER: Number(editForm.shippingIndia || 0) };

    const patch = new FormData();
    patch.append("name", editForm.name);
    patch.append("description", editForm.description);
    patch.append("price", String(Number(editForm.price || 0)));
    patch.append("stockQty", String(Number(editForm.stockQty || 0)));
    patch.append("category", editForm.category);
    patch.append("productType", productType);
    patch.append("salesChannel", editForm.salesChannel);
    patch.append("listingStatus", editForm.listingStatus);
    patch.append("wholesalePrice", String(Number(editForm.wholesalePrice || 0)));
    patch.append("wholesaleMinQty", String(Number(editForm.wholesaleMinQty || 0)));
    patch.append("wholesaleNotes", editForm.wholesaleNotes);
    patch.append("adminNotes", editForm.adminNotes);
    patch.append("featured", String(editForm.featured));
    patch.append("tags", editForm.tags);
    patch.append("videoUrl", editForm.videoUrl);
    patch.append("shippingScope", shippingScope);
    patch.append("shippingRates", JSON.stringify(shippingRates));
    if (editForm.imageFile) {
      patch.append("image", editForm.imageFile);
    }
    const saved = await updateProduct(editProduct, patch);
    if (saved) {
      setEditProduct(null);
      setEditForm(emptyEditForm);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl("");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try {
      await api.delete(`/api/items/${id}`);
      setItems((prev) => prev.filter((item) => item._id !== id));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete product");
    }
  };

  if (loading) return <div style={{ padding: 32 }}>Loading products...</div>;
  if (error) return <div style={{ padding: 32, color: "red" }}>Error: {error}</div>;

  return (
    <div style={{ padding: 32, display: "grid", gap: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0 }}>Product Control Center</h2>
          <p style={{ margin: "6px 0 0", color: "#64748b" }}>
            Filter, approve, edit, and audit every retail and wholesale listing from one place.
          </p>
        </div>
        <button type="button" onClick={fetchItems} style={primaryButton}>Refresh Data</button>
      </div>

      <div style={summaryGrid}>
        {[
          ["Total Listings", summary.total, "all"],
          ["Public on Storefront", summary.publicCount, "public"],
          ["Needs Attention", summary.needsAttention, "not-public"],
          ["In Review", summary.review, "review"],
          ["Low Stock", summary.lowStock, "low-stock"],
          ["Sold Out", summary.soldOut, "sold-out"],
        ].map(([label, value, targetFilter]) => (
          <button
            key={label}
            type="button"
            style={{ ...summaryCard, outline: filter === targetFilter ? "2px solid #0284c7" : "none" }}
            onClick={() => setUrlState({ filter: targetFilter })}
          >
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 8 }}>{value}</div>
            <div style={muted}>Click to inspect</div>
          </button>
        ))}
      </div>

      <div style={toolbar}>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setUrlState({ q: event.target.value });
          }}
          placeholder="Search product, seller, category, state..."
          style={input}
        />
        <select value={filter} onChange={(event) => setUrlState({ filter: event.target.value })} style={input}>
          {FILTERS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select value={sort} onChange={(event) => setUrlState({ sort: event.target.value })} style={input}>
          {SORTS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div style={insightBanner}>
        Showing <strong>{filteredItems.length}</strong> of <strong>{items.length}</strong> products.
        Use this view before approving listings: public visibility depends on seller approval, retail access, listing status, and stock.
      </div>

      {message ? <div style={successBanner}>{message}</div> : null}

      {editProduct ? (
        <div style={modalBackdrop}>
          <section id="admin-product-editor" style={modalCard}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0 }}>Edit Product: {editProduct.name}</h3>
                <p style={{ margin: "6px 0 0", color: "#64748b" }}>
                  Update product details here. The image replacement section is shown first so it is easy to find.
                </p>
              </div>
              <button type="button" onClick={() => setEditProduct(null)} style={ghostButton}>Close</button>
            </div>
            <form onSubmit={saveProductDetails} style={editGrid}>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              {(imagePreviewUrl || editProduct.imageUrl) ? (
                <img
                  src={imagePreviewUrl || editProduct.imageUrl}
                  alt={editForm.name || editProduct.name}
                  style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 14, border: "1px solid #cbd5e1" }}
                />
              ) : (
                <div style={{ width: 120, height: 120, borderRadius: 14, border: "1px dashed #cbd5e1", display: "grid", placeItems: "center", color: "#64748b" }}>
                  No image
                </div>
              )}
              <div style={{ flex: "1 1 260px", display: "grid", gap: 8 }}>
                <label style={{ fontWeight: 700, color: "#0f172a" }}>Replace product image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    if (imagePreviewUrl) {
                      URL.revokeObjectURL(imagePreviewUrl);
                    }
                    setEditForm((prev) => ({ ...prev, imageFile: file }));
                    setImagePreviewUrl(file ? URL.createObjectURL(file) : "");
                  }}
                />
                <div style={muted}>
                  Choose a new image only if you want to replace the current one.
                </div>
              </div>
            </div>
            <input placeholder="Product name" value={editForm.name} onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))} style={input} required />
            <input placeholder="Category" value={editForm.category} onChange={(event) => setEditForm((prev) => ({ ...prev, category: event.target.value }))} style={input} />
            <select value={editForm.productType} onChange={(event) => setEditForm((prev) => ({ ...prev, productType: event.target.value, shippingScope: event.target.value === "live-fish" ? "tamil-nadu" : prev.shippingScope }))} style={input}>
              <option value="live-fish">Live fish</option>
              <option value="accessory">Accessory</option>
            </select>
            <select value={editForm.salesChannel} onChange={(event) => setEditForm((prev) => ({ ...prev, salesChannel: event.target.value }))} style={input}>
              <option value="retail">Retail only</option>
              <option value="wholesale">Wholesale only</option>
              <option value="both">Retail + Wholesale</option>
            </select>
            <input type="number" min="0" placeholder="Retail price" value={editForm.price} onChange={(event) => setEditForm((prev) => ({ ...prev, price: event.target.value }))} style={input} />
            <input type="number" min="0" placeholder="Stock quantity" value={editForm.stockQty} onChange={(event) => setEditForm((prev) => ({ ...prev, stockQty: event.target.value }))} style={input} />
            <input type="number" min="0" placeholder="Wholesale price" value={editForm.wholesalePrice} onChange={(event) => setEditForm((prev) => ({ ...prev, wholesalePrice: event.target.value }))} style={input} />
            <input type="number" min="0" placeholder="Wholesale minimum qty" value={editForm.wholesaleMinQty} onChange={(event) => setEditForm((prev) => ({ ...prev, wholesaleMinQty: event.target.value }))} style={input} />
            <select value={editForm.listingStatus} onChange={(event) => setEditForm((prev) => ({ ...prev, listingStatus: event.target.value }))} style={input}>
              <option value="active">Active</option>
              <option value="review">Needs review</option>
              <option value="hidden">Hidden</option>
            </select>
            <select value={editForm.shippingScope} disabled={editForm.productType === "live-fish"} onChange={(event) => setEditForm((prev) => ({ ...prev, shippingScope: event.target.value }))} style={input}>
              <option value="tamil-nadu">Tamil Nadu only</option>
              <option value="india">All India</option>
            </select>
            {editForm.shippingScope === "tamil-nadu" ? (
              <input type="number" min="0" placeholder="Tamil Nadu shipping" value={editForm.shippingTamilNadu} onChange={(event) => setEditForm((prev) => ({ ...prev, shippingTamilNadu: event.target.value }))} style={input} />
            ) : (
              <input type="number" min="0" placeholder="India shipping" value={editForm.shippingIndia} onChange={(event) => setEditForm((prev) => ({ ...prev, shippingIndia: event.target.value }))} style={input} />
            )}
            <input placeholder="Tags (comma separated)" value={editForm.tags} onChange={(event) => setEditForm((prev) => ({ ...prev, tags: event.target.value }))} style={input} />
            <input placeholder="Video URL" value={editForm.videoUrl} onChange={(event) => setEditForm((prev) => ({ ...prev, videoUrl: event.target.value }))} style={input} />
            <label style={checkLabel}>
              <input type="checkbox" checked={editForm.featured} onChange={(event) => setEditForm((prev) => ({ ...prev, featured: event.target.checked }))} />
              Featured product
            </label>
            <textarea placeholder="Description" value={editForm.description} onChange={(event) => setEditForm((prev) => ({ ...prev, description: event.target.value }))} style={{ ...input, minHeight: 90, gridColumn: "1 / -1" }} />
            <textarea placeholder="Wholesale notes" value={editForm.wholesaleNotes} onChange={(event) => setEditForm((prev) => ({ ...prev, wholesaleNotes: event.target.value }))} style={{ ...input, minHeight: 70 }} />
            <textarea placeholder="Admin notes / checkpoint reason" value={editForm.adminNotes} onChange={(event) => setEditForm((prev) => ({ ...prev, adminNotes: event.target.value }))} style={{ ...input, minHeight: 70 }} />
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="submit" disabled={savingId === editProduct._id} style={primaryButton}>
                {savingId === editProduct._id ? "Saving..." : "Save Product Changes"}
              </button>
              <button type="button" onClick={() => setEditProduct(null)} style={ghostButton}>Cancel</button>
            </div>
            </form>
          </section>
        </div>
      ) : null}

      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={th}>Product</th>
              <th style={th}>Seller</th>
              <th style={th}>Channel</th>
              <th style={th}>Stock</th>
              <th style={th}>Status</th>
              <th style={th}>Public Visibility</th>
              <th style={th}>Checkpoint Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const seller = item.sellerId || {};
              const visibility = item.visibility || {
                public: false,
                label: "Checking",
                reason: "Visibility not calculated yet",
              };
              return (
                <tr key={item._id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{ width: 58, height: 58, borderRadius: 10, objectFit: "cover" }}
                        />
                      ) : null}
                      <div>
                        <strong>{item.name}</strong>
                        <div style={muted}>
                          Rs.{Number(item.price || 0).toFixed(0)}
                          {Number(item.wholesalePrice || 0) > 0
                            ? ` • Bulk Rs.${Number(item.wholesalePrice).toFixed(0)}`
                            : ""}
                        </div>
                        <div style={muted}>{item.productType || "accessory"} • {item.category || "product"}</div>
                        {item.adminNotes ? <div style={{ ...muted, color: "#92400e" }}>Note: {item.adminNotes}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <strong>{seller.storeName || item.sellerName || "Marketplace"}</strong>
                    <div style={muted}>{seller.businessType || "retail"}</div>
                    <div style={muted}>{seller.city || "-"}{seller.state ? `, ${seller.state}` : ""}</div>
                    <div style={muted}>{seller.isApproved === false ? "Seller suspended" : "Seller active"}</div>
                  </td>
                  <td style={td}>
                    <select
                      value={item.salesChannel || "retail"}
                      onChange={(event) => updateProduct(item, { salesChannel: event.target.value })}
                      style={input}
                    >
                      <option value="retail">Retail only</option>
                      <option value="wholesale">Wholesale only</option>
                      <option value="both">Retail + Wholesale</option>
                    </select>
                  </td>
                  <td style={td}>
                    <input
                      type="number"
                      min="0"
                      value={item.stockQty || 0}
                      onChange={(event) => updateProduct(item, { stockQty: event.target.value })}
                      style={{ ...input, width: 90 }}
                    />
                    {Number(item.stockQty || 0) <= 3 ? (
                      <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>
                        {Number(item.stockQty || 0) <= 0 ? "Sold out" : "Check stock"}
                      </div>
                    ) : null}
                  </td>
                  <td style={td}>
                    <select
                      value={item.listingStatus || "active"}
                      onChange={(event) => updateProduct(item, { listingStatus: event.target.value })}
                      style={input}
                    >
                      <option value="active">Active</option>
                      <option value="review">Needs review</option>
                      <option value="hidden">Hidden</option>
                    </select>
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        ...visibilityPill,
                        background: visibility.public ? "#dcfce7" : "#fee2e2",
                        color: visibility.public ? "#166534" : "#991b1b",
                      }}
                    >
                      {visibility.label}
                    </span>
                    <div style={{ ...muted, maxWidth: 220 }}>{visibility.reason}</div>
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <button type="button" onClick={() => startEditProduct(item)}>
                        Edit Details / Image
                      </button>
                      <button
                        type="button"
                        disabled={savingId === item._id}
                        onClick={() => updateProduct(item, { listingStatus: "active" })}
                      >
                        {savingId === item._id ? "Saving..." : "Approve"}
                      </button>
                      <button
                        type="button"
                        disabled={savingId === item._id}
                        onClick={() => updateProduct(item, { listingStatus: "active", salesChannel: "retail" })}
                      >
                        Publish Retail
                      </button>
                      <button
                        type="button"
                        disabled={savingId === item._id}
                        onClick={() => updateProduct(item, { listingStatus: "review" })}
                      >
                        Review
                      </button>
                      <button
                        type="button"
                        disabled={savingId === item._id}
                        onClick={() => updateProduct(item, { listingStatus: "hidden" })}
                      >
                        Hide
                      </button>
                      <button type="button" onClick={() => handleDelete(item._id)} style={dangerButton}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filteredItems.length === 0 ? <p>No products found for this filter.</p> : null}
    </div>
  );
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 1120,
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
};

const th = { padding: "12px 16px", borderBottom: "2px solid #e2e8f0" };
const td = { padding: "12px 16px", verticalAlign: "top" };
const muted = { fontSize: 12, color: "#64748b", marginTop: 4 };
const input = {
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
};
const toolbar = {
  display: "grid",
  gridTemplateColumns: "minmax(260px, 1fr) 220px 220px",
  gap: 12,
};
const successBanner = {
  padding: "12px 14px",
  borderRadius: 14,
  background: "#ecfdf5",
  color: "#047857",
  border: "1px solid #a7f3d0",
  fontWeight: 700,
};
const insightBanner = {
  padding: "12px 14px",
  borderRadius: 14,
  background: "#eff6ff",
  color: "#1e3a8a",
  border: "1px solid #bfdbfe",
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};
const summaryCard = {
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
  borderRadius: 16,
  padding: "14px 16px",
  border: "1px solid #dbeafe",
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};
const editorPanel = {
  background: "#fff",
  border: "1px solid #bae6fd",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 16px 40px rgba(2,132,199,0.12)",
};
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 2000,
};
const modalCard = {
  ...editorPanel,
  width: "min(960px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
};
const editGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
  marginTop: 16,
};
const checkLabel = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
};
const visibilityPill = {
  display: "inline-flex",
  padding: "5px 9px",
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 12,
};
const primaryButton = {
  background: "#075985",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "9px 12px",
  fontWeight: 800,
  cursor: "pointer",
};
const ghostButton = {
  background: "#fff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "9px 12px",
  fontWeight: 700,
  cursor: "pointer",
};
const dangerButton = {
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "6px 10px",
};

export default AdminProducts;
